import { useState, useMemo, useEffect, createElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import MapComponent from '../components/Map';
import { useDevicesStore, Device } from '../store/devices.store';
import { groupsApi, geofencesApi, devicesApi } from '../services/api';
import { connectSocket, disconnectSocket } from '../services/socket';
import { useTenantContextStore } from '../store/tenant-context.store';
import { useOfflineDevices } from '../hooks/useOfflineDevices';
import { useIsOnline } from '../hooks/useOfflineStatus';
import { OfflineDataBadge } from '../components/OfflineDataBadge';
import { formatDistanceToNow } from 'date-fns';
import { fr, de, enUS, it } from 'date-fns/locale';

const DATE_LOCALES: Record<string, typeof fr> = { fr, de, en: enUS, it };
import {
  Search,
  ChevronRight,
  ChevronLeft,
  Wifi,
  WifiOff,
  Shield,
  Filter,
  Target,
  Truck,
  Car,
  Bike,
  Bus,
  Ship,
  Plane,
  Tractor,
  Forklift,
  Smartphone,
  Gauge,
  Clock,
  Battery,
  BatteryCharging,
  Navigation,
  LucideIcon,
} from 'lucide-react';

// Mapping des icônes de véhicules
const VEHICLE_ICONS: Record<string, LucideIcon> = {
  car: Car,
  truck: Truck,
  motorcycle: Bike,
  bus: Bus,
  van: Truck,
  tractor: Tractor,
  forklift: Forklift,
  boat: Ship,
  plane: Plane,
  smartphone: Smartphone,
  phone: Smartphone,
  default: Truck,
};

function getVehicleIcon(iconName?: string): LucideIcon {
  if (!iconName) return Truck;
  return VEHICLE_ICONS[iconName.toLowerCase()] || Truck;
}
import clsx from 'clsx';
import { useBreakpoint } from '../hooks/useBreakpoint';

type StatusFilter = 'all' | 'online' | 'offline';

export default function MapPage() {
  const { t } = useTranslation();
  const { devices, selectedDeviceId, selectDevice, setDevices } = useDevicesStore();
  const { selectedTenant } = useTenantContextStore();
  const tenantKey = selectedTenant?.id || 'default';
  const { isMobile } = useBreakpoint();
  // On mobile/tablet, sidebar is closed by default
  const [sidebarOpen, setSidebarOpen] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [showGeofences, setShowGeofences] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Offline support
  const isOnline = useIsOnline();
  const {
    devices: offlineDevices,
    geofences: offlineGeofences,
    isFromCache,
    lastSyncAt,
    refreshFromNetwork,
  } = useOfflineDevices();

  // Fetch devices (only when online)
  const { data: devicesData } = useQuery({
    queryKey: ['devices', tenantKey],
    queryFn: async () => {
      const response = await devicesApi.list({ limit: 1000 });
      return response.data;
    },
    enabled: isOnline,
  });

  // Connect to WebSocket for real-time updates (only when online)
  // Reconnect when tenant changes so real-time updates match the selected tenant
  useEffect(() => {
    if (!isOnline) return;
    try {
      disconnectSocket();
      connectSocket();
    } catch (err) {
      console.error('Failed to connect WebSocket:', err);
    }
  }, [isOnline, tenantKey]);

  // Update store when devices are loaded (from API or cache)
  useEffect(() => {
    if (devicesData?.data) {
      setDevices(devicesData.data);
    } else if (offlineDevices.length > 0 && !isOnline) {
      // Use cached data when offline
      setDevices(offlineDevices);
    }
  }, [devicesData, offlineDevices, isOnline, setDevices]);

  // Handle manual refresh
  const handleRefresh = async () => {
    if (!isOnline) return;
    setIsRefreshing(true);
    try {
      await refreshFromNetwork();
    } finally {
      setIsRefreshing(false);
    }
  };

  // Fetch groups (only when online)
  const { data: groupsData } = useQuery({
    queryKey: ['groups', tenantKey],
    queryFn: () => groupsApi.list(),
    enabled: isOnline,
  });
  const groups = groupsData?.data?.data || [];

  // Fetch geofences (only when online)
  const { data: geofencesData } = useQuery({
    queryKey: ['geofences', tenantKey],
    queryFn: () => geofencesApi.list(),
    enabled: isOnline,
  });
  // Use API data or fall back to offline cache
  const geofences = geofencesData?.data?.data || (isFromCache ? offlineGeofences : []);

  // Filter devices
  const filteredDevices = useMemo(() => {
    if (!Array.isArray(devices)) return [];
    return devices.filter((d) => {
      // Search filter
      const matchesSearch =
        d.name.toLowerCase().includes(search.toLowerCase()) ||
        d.plate?.toLowerCase().includes(search.toLowerCase()) ||
        d.imei.includes(search);

      // Status filter
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'online' && d.status === 'ONLINE') ||
        (statusFilter === 'offline' && d.status === 'OFFLINE');

      // Group filter
      const matchesGroup =
        selectedGroups.length === 0 || selectedGroups.includes(d.group?.id || '');

      return matchesSearch && matchesStatus && matchesGroup;
    });
  }, [devices, search, statusFilter, selectedGroups]);

  const handleDeviceClick = (device: Device) => {
    // Force re-center even if same device is already selected
    selectDevice(null);
    if (isMobile) {
      // Close sidebar first, wait for animation (300ms) + resize, then select
      setSidebarOpen(false);
      setTimeout(() => selectDevice(device.id), 400);
    } else {
      setTimeout(() => selectDevice(device.id), 0);
    }
  };

  const toggleGroup = (groupId: string) => {
    setSelectedGroups((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  const stats = useMemo(() => {
    if (!Array.isArray(devices)) return { total: 0, online: 0, offline: 0, moving: 0 };
    const online = devices.filter((d) => d.status === 'ONLINE').length;
    const offline = devices.length - online;
    const moving = devices.filter(
      (d) => d.status === 'ONLINE' && d.lastPosition && d.lastPosition.speed > 0
    ).length;
    return { total: devices.length, online, offline, moving };
  }, [devices]);

  return (
    <div className={clsx("flex relative overflow-hidden", isMobile ? "-mx-4 -mt-4 -mb-20 h-[calc(100dvh-7.5rem)]" : "h-[calc(100vh-88px)]")}>
      {/* Sidebar */}
      <aside
        className={clsx(
          'bg-white border-r border-gray-200 transition-all duration-300 flex flex-col z-20',
          sidebarOpen ? 'w-80' : 'w-0'
        )}
      >
        {sidebarOpen && (
          <>
            {/* Header */}
            <div className="p-4 border-b border-gray-200">
              {/* Offline indicator */}
              {isFromCache && (
                <OfflineDataBadge
                  isFromCache={isFromCache}
                  lastSyncAt={lastSyncAt}
                  onRefresh={isOnline ? handleRefresh : undefined}
                  isRefreshing={isRefreshing}
                  className="mb-3"
                />
              )}

              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Truck className="h-5 w-5 text-green-600" />
                  <h2 className="font-semibold text-gray-900">{t('map.sidebar.vehicles')}</h2>
                  <span className="text-xs text-gray-500">({filteredDevices.length})</span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={clsx(
                      'p-1.5 rounded-lg transition-colors',
                      showFilters
                        ? 'bg-green-100 text-green-600'
                        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                    )}
                  >
                    <Filter className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setSidebarOpen(false)}
                    className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500/20"
                />
              </div>

              {/* Filters Panel */}
              {showFilters && (
                <div className="mt-3 p-3 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
                  {/* Status filter */}
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Statut</p>
                    <div className="flex gap-1">
                      {[
                        { value: 'all', label: 'Tous' },
                        { value: 'online', label: 'En ligne' },
                        { value: 'offline', label: 'Hors ligne' },
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => setStatusFilter(option.value as StatusFilter)}
                          className={clsx(
                            'px-3 py-1 text-xs rounded-lg transition-colors',
                            statusFilter === option.value
                              ? 'bg-green-100 text-green-700 border border-green-200'
                              : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                          )}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Groups filter */}
                  {groups.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">Groupes</p>
                      <div className="flex flex-wrap gap-1">
                        {groups.map((group: { id: string; name: string; color: string }) => (
                          <button
                            key={group.id}
                            onClick={() => toggleGroup(group.id)}
                            className={clsx(
                              'px-2 py-1 text-xs rounded-lg transition-colors flex items-center gap-1',
                              selectedGroups.includes(group.id)
                                ? 'bg-green-100 text-green-700 border border-green-200'
                                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                            )}
                          >
                            <span
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: group.color }}
                            />
                            {group.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Geofences toggle */}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-gray-500" />
                      <span className="text-xs text-gray-700">Afficher zones</span>
                    </div>
                    <button
                      onClick={() => setShowGeofences(!showGeofences)}
                      className={clsx(
                        'relative w-9 h-5 rounded-full transition-colors',
                        showGeofences ? 'bg-green-500' : 'bg-gray-300'
                      )}
                    >
                      <span
                        className={clsx(
                          'absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow-sm',
                          showGeofences ? 'left-4' : 'left-0.5'
                        )}
                      />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Device list */}
            <div className="flex-1 overflow-y-auto no-scrollbar">
              {filteredDevices.map((device) => (
                <DeviceListItem
                  key={device.id}
                  device={device}
                  isSelected={device.id === selectedDeviceId}
                  onClick={() => handleDeviceClick(device)}
                />
              ))}
              {filteredDevices.length === 0 && (
                <div className="p-8 text-center">
                  <Truck className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">{t('map.no_vehicle_found')}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {t('map.modify_filters')}
                  </p>
                </div>
              )}
            </div>

            {/* Stats footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="grid grid-cols-4 gap-2">
                <div className="text-center">
                  <p className="text-lg font-semibold text-gray-900">{stats.total}</p>
                  <p className="text-[10px] text-gray-500">Total</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-green-600">{stats.online}</p>
                  <p className="text-[10px] text-gray-500">En ligne</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-gray-400">{stats.offline}</p>
                  <p className="text-[10px] text-gray-500">Hors ligne</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-blue-600">{stats.moving}</p>
                  <p className="text-[10px] text-gray-500">En mouv.</p>
                </div>
              </div>
            </div>
          </>
        )}
      </aside>

      {/* Toggle sidebar button */}
      {!sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          className="absolute left-4 top-4 z-20 bg-white p-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors shadow-md"
        >
          <ChevronRight className="h-5 w-5 text-gray-700" />
        </button>
      )}

      {/* Map */}
      <div className="flex-1 relative">
        <MapComponent
          devices={filteredDevices}
          geofences={showGeofences ? geofences : []}
          onDeviceClick={handleDeviceClick}
          selectedDeviceId={selectedDeviceId}
        />

        {/* Map overlay info */}
        <div className="absolute top-4 right-4 z-10">
          <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-md">
            <div className="flex items-center gap-3 text-sm">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-gray-700">{stats.online} en ligne</span>
              </div>
              <div className="w-px h-4 bg-gray-200" />
              <div className="flex items-center gap-1.5">
                <Target className="h-3.5 w-3.5 text-purple-500" />
                <span className="text-gray-700">{geofences.length} zones</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DeviceListItem({
  device,
  isSelected,
  onClick,
}: {
  device: Device;
  isSelected: boolean;
  onClick: () => void;
}) {
  const { t, i18n } = useTranslation();
  const dateLocale = DATE_LOCALES[i18n.language?.split('-')[0] ?? 'fr'] ?? fr;
  const isOnline = device.status === 'ONLINE';
  const isPrivate = device.privacyActive === true;
  const isMoving = isOnline && !isPrivate && device.lastPosition && device.lastPosition.speed > 0;
  const lastUpdate = device.lastPositionAt
    ? formatDistanceToNow(new Date(device.lastPositionAt), { addSuffix: true, locale: dateLocale })
    : t('map.sidebar.never');

  // Get the vehicle icon based on device.icon setting
  const vehicleIcon = getVehicleIcon(device.icon);
  // Privacy wins over online/offline: it explains the silence, so the card
  // doesn't read as a broken tracker.
  const iconColor = isPrivate ? '#6366f1' : device.markerColor || (isOnline ? '#10b981' : '#9ca3af');

  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full p-4 text-left border-b border-gray-100 hover:bg-gray-50 transition-colors',
        isSelected && 'bg-green-50 border-l-2 border-l-green-500'
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${iconColor}20` }}
            >
              {createElement(vehicleIcon, {
                className: 'h-5 w-5',
                style: { color: iconColor },
              })}
            </div>
            {isPrivate ? (
              <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-indigo-500 border-2 border-white" />
            ) : isOnline && (
              <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-500 border-2 border-white" />
            )}
          </div>
          <div>
            <div className="font-medium text-gray-900">{device.name}</div>
            <div className="flex items-center gap-2">
              {device.plate && (
                <span className="text-xs text-gray-500">{device.plate}</span>
              )}
              {device.group && (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded-full"
                  style={{
                    backgroundColor: `${device.group.color}20`,
                    color: device.group.color,
                  }}
                >
                  {device.group.name}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {isPrivate ? (
            <Shield className="h-4 w-4 text-indigo-500" />
          ) : isOnline ? (
            <Wifi className="h-4 w-4 text-green-500" />
          ) : (
            <WifiOff className="h-4 w-4 text-gray-400" />
          )}
          {isPrivate && (
            <span className="text-[10px] font-medium text-indigo-600">Confidentialité</span>
          )}
        </div>
      </div>

      {device.lastPosition && (
        <div className="mt-3 space-y-2">
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="flex items-center gap-1 text-gray-500">
              <Gauge className="h-3 w-3" />
              <span className={clsx(isMoving ? 'text-green-600 font-medium' : '')}>
                {Math.round(device.lastPosition.speed)} km/h
              </span>
            </div>
            <div className="flex items-center gap-1 text-gray-500">
              <Clock className="h-3 w-3" />
              <span className="truncate">{lastUpdate}</span>
            </div>
            {device.lastPosition.odometer && (
              <div className="flex items-center gap-1 text-gray-500">
                <Navigation className="h-3 w-3" />
                <span>{Math.round(device.lastPosition.odometer / 1000)} km</span>
              </div>
            )}
          </div>
          {(device.lastPosition.externalVoltage || device.lastPosition.batteryVoltage) && (
            <div className="grid grid-cols-2 gap-2 text-xs">
              {device.lastPosition.externalVoltage && (
                <div className="flex items-center gap-1 text-gray-500">
                  <Battery className="h-3 w-3" />
                  <span>{(device.lastPosition.externalVoltage / 1000).toFixed(1)}V</span>
                </div>
              )}
              {device.lastPosition.batteryVoltage && (
                <div className="flex items-center gap-1 text-gray-500">
                  <BatteryCharging className="h-3 w-3" />
                  <span>{(device.lastPosition.batteryVoltage / 1000).toFixed(2)}V</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {isMoving && (
        <div className="mt-2 flex items-center gap-1">
          <span className="px-2 py-0.5 text-[10px] font-medium bg-green-100 text-green-700 rounded-full">
            En mouvement
          </span>
        </div>
      )}
    </button>
  );
}
